import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Alert,
    CircularProgress,
    Pagination,
    Chip,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    IconButton,
    Collapse,
    Tabs,
    Tab,
    InputAdornment,
    Divider,
    Stack,
    Tooltip,
    Badge
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    FilterList as FilterIcon,
    Download as DownloadIcon,
    Clear as ClearIcon,
    KeyboardArrowDown as ExpandMoreIcon,
    KeyboardArrowUp as ExpandLessIcon,
    Person as PersonIcon,
    LocationOn as LocationIcon,
    Schedule as ScheduleIcon,
    AccountBalanceWallet as WalletIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    ViewList as ViewListIcon,
    ViewModule as ViewModuleIcon,
    ContentCopy as CopyIcon
} from '@mui/icons-material';
import { explorerApi } from '../services/api';
import { transformToBTC } from '../../utils/helpers';

const Explorer = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Data state
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Pagination state
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [limit, setLimit] = useState(50);

    // Filter state
    const [activeTab, setActiveTab] = useState(0); // 0: All, 1: Deposits, 2: Withdrawals
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filters, setFilters] = useState({
        search: '',
        searchType: 'user', // user, address, txId
        minAmount: '',
        maxAmount: '',
        startDate: null,
        endDate: null,
        sortBy: 'time',
        sortOrder: 'desc'
    });

    // Applied filters for display
    const [appliedFilters, setAppliedFilters] = useState({});

    // View state
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'

    // Statistics
    const [stats, setStats] = useState({
        total: 0,
        deposits: 0,
        withdrawals: 0,
        totalVolume: 0
    });

    // Fetch data
    const fetchTransactions = useCallback(async (newFilters = filters, newPage = page) => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                page: newPage,
                limit,
                search: newFilters.search,
                searchType: newFilters.searchType,
                minAmount: newFilters.minAmount,
                maxAmount: newFilters.maxAmount,
                startDate: newFilters.startDate?.toISOString(),
                endDate: newFilters.endDate?.toISOString(),
                sortBy: newFilters.sortBy,
                sortOrder: newFilters.sortOrder
            };

            // Remove empty params
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) {
                    delete params[key];
                }
            });

            let depositsPromise = Promise.resolve({ data: { data: [], pagination: { total: 0, totalPages: 0 } } });
            let withdrawalsPromise = Promise.resolve({ data: { data: [], pagination: { total: 0, totalPages: 0 } } });

            // Fetch based on active tab
            if (activeTab === 0) { // All transactions - fetch both
                depositsPromise = explorerApi.getDeposits(params);
                withdrawalsPromise = explorerApi.getWithdrawals(params);
            } else if (activeTab === 1) { // Deposits only
                depositsPromise = explorerApi.getDeposits(params);
            } else if (activeTab === 2) { // Withdrawals only
                withdrawalsPromise = explorerApi.getWithdrawals(params);
            }

            const [depositsRes, withdrawalsRes] = await Promise.all([depositsPromise, withdrawalsPromise]);

            let allTransactions = [];
            let totalCount = 0;
            let totalPagesCount = 0;

            if (activeTab === 0) { // All transactions
                const deposits = depositsRes.data.data.map(tx => ({ ...tx, type: 'deposit' }));
                const withdrawals = withdrawalsRes.data.data.map(tx => ({ ...tx, type: 'withdrawal' }));
                allTransactions = [...deposits, ...withdrawals];

                // Sort combined results
                allTransactions.sort((a, b) => {
                    const aVal = a[newFilters.sortBy];
                    const bVal = b[newFilters.sortBy];
                    const comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
                    return newFilters.sortOrder === 'desc' ? -comparison : comparison;
                });

                totalCount = (depositsRes.data.pagination?.total || 0) + (withdrawalsRes.data.pagination?.total || 0);
                totalPagesCount = Math.max(depositsRes.data.pagination?.totalPages || 0, withdrawalsRes.data.pagination?.totalPages || 0);
            } else if (activeTab === 1) { // Deposits only
                allTransactions = depositsRes.data.data.map(tx => ({ ...tx, type: 'deposit' }));
                totalCount = depositsRes.data.pagination?.total || 0;
                totalPagesCount = depositsRes.data.pagination?.totalPages || 0;
            } else { // Withdrawals only
                allTransactions = withdrawalsRes.data.data.map(tx => ({ ...tx, type: 'withdrawal' }));
                totalCount = withdrawalsRes.data.pagination?.total || 0;
                totalPagesCount = withdrawalsRes.data.pagination?.totalPages || 0;
            }

            setTransactions(allTransactions);
            setTotalResults(totalCount);
            setTotalPages(totalPagesCount);

            // Update stats
            const totalVolume = allTransactions.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);
            setStats({
                total: allTransactions.length,
                deposits: allTransactions.filter(tx => tx.type === 'deposit').length,
                withdrawals: allTransactions.filter(tx => tx.type === 'withdrawal').length,
                totalVolume
            });

        } catch (err) {
            console.error('Error fetching transactions:', err);
            setError('Failed to load transactions. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [filters, page, limit, activeTab]);

    // Initial load
    useEffect(() => {
        fetchTransactionsWithTab(activeTab, filters, page);
    }, []);

    // Trigger fetch when activeTab changes
    useEffect(() => {
        if (activeTab !== undefined) {
            fetchTransactionsWithTab(activeTab, filters, 1);
        }
    }, [activeTab]);

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            const newFilters = { ...prev, [key]: value };

            // If sortBy changes, ensure sortOrder is valid
            if (key === 'sortBy') {
                // Keep the same logical order (desc for newest/maximum, asc for oldest/minimum)
                // No need to change the actual value, just let the UI render correctly
            }

            return newFilters;
        });
    };

    // Apply filters
    const applyFilters = () => {
        setAppliedFilters({ ...filters });
        setPage(1);
        fetchTransactionsWithTab(activeTab, filters, 1);
        setFiltersOpen(false);
    };

    // Clear filters
    const clearFilters = () => {
        const defaultFilters = {
            search: '',
            searchType: 'user',
            minAmount: '',
            maxAmount: '',
            startDate: null,
            endDate: null,
            sortBy: 'time',
            sortOrder: 'desc'
        };
        setFilters(defaultFilters);
        setAppliedFilters({});
        setPage(1);
        fetchTransactionsWithTab(activeTab, defaultFilters, 1);
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setPage(1);
        // Use a more reliable method to fetch data with the new tab value
        // We'll pass the new activeTab value directly to avoid state timing issues
        setTimeout(() => {
            fetchTransactionsWithTab(newValue, filters, 1);
        }, 0);
    };

    // Helper function to fetch transactions with explicit tab value
    const fetchTransactionsWithTab = async (tabValue, currentFilters = filters, newPage = page) => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                page: newPage,
                limit,
                search: currentFilters.search,
                searchType: currentFilters.searchType,
                minAmount: currentFilters.minAmount,
                maxAmount: currentFilters.maxAmount,
                startDate: currentFilters.startDate?.toISOString(),
                endDate: currentFilters.endDate?.toISOString(),
                sortBy: currentFilters.sortBy,
                sortOrder: currentFilters.sortOrder
            };

            // Remove empty params
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) {
                    delete params[key];
                }
            });

            let depositsPromise = Promise.resolve({ data: { data: [], pagination: { total: 0, totalPages: 0 } } });
            let withdrawalsPromise = Promise.resolve({ data: { data: [], pagination: { total: 0, totalPages: 0 } } });

            // Fetch based on active tab
            if (tabValue === 0) { // All transactions - fetch both
                depositsPromise = explorerApi.getDeposits(params);
                withdrawalsPromise = explorerApi.getWithdrawals(params);
            } else if (tabValue === 1) { // Deposits only
                depositsPromise = explorerApi.getDeposits(params);
            } else if (tabValue === 2) { // Withdrawals only
                withdrawalsPromise = explorerApi.getWithdrawals(params);
            }

            const [depositsRes, withdrawalsRes] = await Promise.all([depositsPromise, withdrawalsPromise]);

            let allTransactions = [];
            let totalCount = 0;
            let totalPagesCount = 0;

            if (tabValue === 0) { // All transactions
                const deposits = depositsRes.data.data.map(tx => ({ ...tx, type: 'deposit' }));
                const withdrawals = withdrawalsRes.data.data.map(tx => ({ ...tx, type: 'withdrawal' }));
                allTransactions = [...deposits, ...withdrawals];

                // Sort combined results
                allTransactions.sort((a, b) => {
                    const aVal = a[currentFilters.sortBy];
                    const bVal = b[currentFilters.sortBy];
                    const comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
                    return currentFilters.sortOrder === 'desc' ? -comparison : comparison;
                });

                totalCount = (depositsRes.data.pagination?.total || 0) + (withdrawalsRes.data.pagination?.total || 0);
                totalPagesCount = Math.max(depositsRes.data.pagination?.totalPages || 0, withdrawalsRes.data.pagination?.totalPages || 0);
            } else if (tabValue === 1) { // Deposits only
                allTransactions = depositsRes.data.data.map(tx => ({ ...tx, type: 'deposit' }));
                totalCount = depositsRes.data.pagination?.total || 0;
                totalPagesCount = depositsRes.data.pagination?.totalPages || 0;
            } else { // Withdrawals only
                allTransactions = withdrawalsRes.data.data.map(tx => ({ ...tx, type: 'withdrawal' }));
                totalCount = withdrawalsRes.data.pagination?.total || 0;
                totalPagesCount = withdrawalsRes.data.pagination?.totalPages || 0;
            }

            setTransactions(allTransactions);
            setTotalResults(totalCount);
            setTotalPages(totalPagesCount);

            // Update stats
            const totalVolume = allTransactions.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);
            setStats({
                total: allTransactions.length,
                deposits: allTransactions.filter(tx => tx.type === 'deposit').length,
                withdrawals: allTransactions.filter(tx => tx.type === 'withdrawal').length,
                totalVolume
            });

        } catch (err) {
            console.error('Error fetching transactions:', err);
            setError('Failed to load transactions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle sort
    const handleSort = (column) => {
        const newOrder = filters.sortBy === column && filters.sortOrder === 'asc' ? 'desc' : 'asc';
        const newFilters = { ...filters, sortBy: column, sortOrder: newOrder };
        setFilters(newFilters);
        fetchTransactionsWithTab(activeTab, newFilters, page);
    };

    // Format helpers
    const formatBTC = transformToBTC;
    const formatDate = (dateString) => new Date(dateString).toLocaleString();
    const truncateAddress = (address, length = 12) => {
        if (!address) return '';
        return `${ address.slice(0, length) }...${ address.slice(-4) }`;
    };

    // Copy to clipboard helper
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    // Export to CSV
    const exportToCSV = () => {
        const headers = ['Date', 'Type', 'Amount (BTC)', 'User', 'Address', 'Transaction ID'];
        const csvData = transactions.map(tx => [
            formatDate(tx.time),
            tx.type,
            formatBTC(tx.amount),
            tx.user,
            tx.type === 'deposit' ?
                (tx.senderAddresses && tx.senderAddresses.length > 0 ?
                    tx.senderAddresses.join('; ') :
                    tx.address || '') :
                tx.withdrawAddress,
            tx.txId
        ]);

        const csvContent = [headers, ...csvData]
            .map(row => row.map(field => `"${ field }"`).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `transactions-${ new Date().toISOString().split('T')[0] }.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Get active filter count
    const getActiveFilterCount = () => {
        return Object.entries(appliedFilters).filter(([key, value]) => {
            if (key === 'sortBy' || key === 'sortOrder') return false;
            return value !== '' && value !== null && value !== undefined;
        }).length;
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
                    🔍 {t('explorer.transactionExplorer')}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                    {t('explorer.transactionExplorerSubtitle')}
                </Typography>
            </Box>

            {/* Filter Indicator */}
            <Alert
                severity="info"
                icon={<FilterIcon />}
                sx={{
                    mb: 3,
                    bgcolor: 'rgba(33, 150, 243, 0.08)',
                    border: '1px solid rgba(33, 150, 243, 0.2)',
                    '& .MuiAlert-icon': {
                        color: 'info.main'
                    }
                }}
            >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    💡 <strong>{t('explorer.proTip')}:</strong> {t('explorer.proTipDescription')}
                </Typography>
            </Alert>

            {/* Quick Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ py: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WalletIcon sx={{ color: 'primary.main' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {stats.total.toLocaleString()}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {t('explorer.totalTransactions')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ py: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrendingUpIcon sx={{ color: 'success.main' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {stats.deposits.toLocaleString()}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {t('explorer.deposits')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ py: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrendingDownIcon sx={{ color: 'warning.main' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {stats.withdrawals.toLocaleString()}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {t('explorer.withdrawals')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ py: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main' }}>
                                    ₿
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {formatBTC(stats.totalVolume)}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {t('explorer.totalVolumeBTC')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Main Content */}
            <Card>
                <CardContent sx={{ p: 0 }}>
                    {/* Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={activeTab} onChange={handleTabChange} sx={{ px: 3, pt: 2 }}>
                            <Tab label={t('explorer.allTransactions')} />
                            <Tab label={t('explorer.depositsOnly')} />
                            <Tab label={t('explorer.withdrawalsOnly')} />
                        </Tabs>
                    </Box>

                    {/* Filters */}
                    <Box sx={{ p: 3, pb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={
                                    <Badge badgeContent={getActiveFilterCount()} color="primary">
                                        <FilterIcon />
                                    </Badge>
                                }
                                endIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                onClick={() => setFiltersOpen(!filtersOpen)}
                                sx={{
                                    // Add pulsing animation when no filters are active
                                    ...(getActiveFilterCount() === 0 && {
                                        animation: 'pulse 2s infinite',
                                        '@keyframes pulse': {
                                            '0%': {
                                                boxShadow: '0 0 0 0 rgba(255, 111, 0, 0.7)',
                                                borderColor: 'rgba(255, 111, 0, 0.5)',
                                            },
                                            '70%': {
                                                boxShadow: '0 0 0 10px rgba(255, 111, 0, 0)',
                                                borderColor: 'rgba(255, 111, 0, 0.8)',
                                            },
                                            '100%': {
                                                boxShadow: '0 0 0 0 rgba(255, 111, 0, 0)',
                                                borderColor: 'rgba(255, 111, 0, 0.5)',
                                            },
                                        },
                                    }),
                                    // Enhanced hover effect
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(255, 111, 0, 0.3)',
                                        transition: 'all 0.2s ease',
                                    },
                                }}
                            >
                                Filters
                            </Button>

                            {getActiveFilterCount() > 0 && (
                                <Button
                                    variant="text"
                                    startIcon={<ClearIcon />}
                                    onClick={clearFilters}
                                    size="small"
                                >
                                    {t('explorer.clearAll')}
                                </Button>
                            )}

                            <Box sx={{ flexGrow: 1 }} />

                            {/* View Toggle Buttons */}
                            <Box sx={{ display: 'flex', border: '1px solid rgba(255, 111, 0, 0.3)', borderRadius: 1, overflow: 'hidden' }}>
                                <Tooltip title="Table View">
                                    <IconButton
                                        onClick={() => setViewMode('table')}
                                        sx={{
                                            color: viewMode === 'table' ? 'primary.main' : 'text.secondary',
                                            bgcolor: viewMode === 'table' ? 'rgba(255, 111, 0, 0.1)' : 'transparent',
                                            borderRadius: 0,
                                            px: 2,
                                            '&:hover': {
                                                bgcolor: 'rgba(255, 111, 0, 0.08)',
                                            },
                                        }}
                                    >
                                        <ViewListIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Grid View">
                                    <IconButton
                                        onClick={() => setViewMode('grid')}
                                        sx={{
                                            color: viewMode === 'grid' ? 'primary.main' : 'text.secondary',
                                            bgcolor: viewMode === 'grid' ? 'rgba(255, 111, 0, 0.1)' : 'transparent',
                                            borderRadius: 0,
                                            px: 2,
                                            '&:hover': {
                                                bgcolor: 'rgba(255, 111, 0, 0.08)',
                                            },
                                        }}
                                    >
                                        <ViewModuleIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={() => fetchTransactionsWithTab(activeTab, filters, page)}
                                disabled={loading}
                            >
                                {t('explorer.refresh')}
                            </Button>

                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={exportToCSV}
                                disabled={loading || transactions.length === 0}
                            >
                                {t('explorer.exportCSV')}
                            </Button>
                        </Box>

                        {/* Applied Filters Display */}
                        {getActiveFilterCount() > 0 && (
                            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                                {Object.entries(appliedFilters).map(([key, value]) => {
                                    if (!value || key === 'sortBy' || key === 'sortOrder') return null;

                                    let label = '';
                                    let displayValue = value;

                                    switch (key) {
                                        case 'search':
                                            label = `Search (${ appliedFilters.searchType })`;
                                            break;
                                        case 'minAmount':
                                            label = 'Min Amount';
                                            displayValue = `${ value } BTC`;
                                            break;
                                        case 'maxAmount':
                                            label = 'Max Amount';
                                            displayValue = `${ value } BTC`;
                                            break;
                                        case 'startDate':
                                            label = 'From';
                                            displayValue = new Date(value).toLocaleDateString();
                                            break;
                                        case 'endDate':
                                            label = 'To';
                                            displayValue = new Date(value).toLocaleDateString();
                                            break;
                                        default:
                                            return null;
                                    }

                                    return (
                                        <Chip
                                            key={key}
                                            label={`${ label }: ${ displayValue }`}
                                            variant="outlined"
                                            size="small"
                                            onDelete={() => {
                                                const newFilters = { ...filters, [key]: key.includes('Date') ? null : '' };
                                                setFilters(newFilters);
                                                setAppliedFilters(newFilters);
                                                fetchTransactionsWithTab(activeTab, newFilters, 1);
                                            }}
                                        />
                                    );
                                })}
                            </Stack>
                        )}

                        {/* Filter Panel */}
                        <Collapse in={filtersOpen}>
                            <Paper sx={{ p: 3, mt: 2, bgcolor: 'background.paper' }}>
                                <Grid container spacing={3}>
                                    {/* Search */}
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label={t('common.search')}
                                            value={filters.search}
                                            onChange={(e) => handleFilterChange('search', e.target.value)}
                                            placeholder={t('explorer.searchPlaceholder')}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>{t('explorer.searchType')}</InputLabel>
                                            <Select
                                                value={filters.searchType}
                                                onChange={(e) => handleFilterChange('searchType', e.target.value)}
                                                label={t('explorer.searchType')}
                                            >
                                                <MenuItem value="user">{t('explorer.userID')}</MenuItem>
                                                <MenuItem value="address">{t('explorer.address')}</MenuItem>
                                                <MenuItem value="txId">{t('explorer.txID')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {/* Amount Range */}
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label={t('explorer.minAmountBTC')}
                                            type="number"
                                            value={filters.minAmount}
                                            onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                                            inputProps={{ step: 0.00000001, min: 0 }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label={t('explorer.maxAmountBTC')}
                                            type="number"
                                            value={filters.maxAmount}
                                            onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                                            inputProps={{ step: 0.00000001, min: 0 }}
                                        />
                                    </Grid>

                                    {/* Date Range */}
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label={t('explorer.startDate')}
                                            type="datetime-local"
                                            value={filters.startDate ? filters.startDate.toISOString().slice(0, 16) : ''}
                                            onChange={(e) => handleFilterChange('startDate', e.target.value ? new Date(e.target.value) : null)}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            inputProps={{
                                                max: filters.endDate ? filters.endDate.toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16)
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label={t('explorer.endDate')}
                                            type="datetime-local"
                                            value={filters.endDate ? filters.endDate.toISOString().slice(0, 16) : ''}
                                            onChange={(e) => handleFilterChange('endDate', e.target.value ? new Date(e.target.value) : null)}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            inputProps={{
                                                min: filters.startDate ? filters.startDate.toISOString().slice(0, 16) : '',
                                                max: new Date().toISOString().slice(0, 16)
                                            }}
                                        />
                                    </Grid>

                                    {/* Sort Options */}
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>{t('explorer.sortBy')}</InputLabel>
                                            <Select
                                                value={filters.sortBy}
                                                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                                label={t('explorer.sortBy')}
                                            >
                                                <MenuItem value="time">{t('common.date')}</MenuItem>
                                                <MenuItem value="amount">{t('explorer.amount')}</MenuItem>
                                                <MenuItem value="user">{t('explorer.user')}</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>{t('explorer.sortOrder')}</InputLabel>
                                            <Select
                                                value={filters.sortOrder}
                                                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                                                label={t('explorer.sortOrder')}
                                            >
                                                <MenuItem value="desc">
                                                    {filters.sortBy === 'amount' ? t('explorer.maximumFirst') : t('explorer.newestFirst')}
                                                </MenuItem>
                                                <MenuItem value="asc">
                                                    {filters.sortBy === 'amount' ? t('explorer.minimumFirst') : t('explorer.oldestFirst')}
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {/* Action Buttons */}
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="outlined"
                                                onClick={() => setFilters({
                                                    search: '',
                                                    searchType: 'user',
                                                    minAmount: '',
                                                    maxAmount: '',
                                                    startDate: null,
                                                    endDate: null,
                                                    sortBy: 'time',
                                                    sortOrder: 'desc'
                                                })}
                                            >
                                                {t('common.reset')}
                                            </Button>
                                            <Button
                                                variant="contained"
                                                onClick={applyFilters}
                                                disabled={loading}
                                            >
                                                {t('explorer.applyFilters')}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Collapse>
                    </Box>

                    <Divider />

                    {/* Error Display */}
                    {error && (
                        <Box sx={{ p: 3 }}>
                            <Alert severity="error" onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        </Box>
                    )}

                    {/* Results Info */}
                    <Box sx={{ p: 3, pb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('explorer.showing')} {transactions.length} {t('explorer.of')} {totalResults.toLocaleString()} {t('explorer.transactionsLower')}
                            {activeTab === 1 && ` (${ t('explorer.depositsOnly') })`}
                            {activeTab === 2 && ` (${ t('explorer.withdrawalsOnly') })`}
                        </Typography>
                    </Box>

                    {/* Transaction Table */}
                    {viewMode === 'table' ? (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={filters.sortBy === 'time'}
                                                direction={filters.sortBy === 'time' ? filters.sortOrder : 'asc'}
                                                onClick={() => handleSort('time')}
                                            >
                                                {t('explorer.dateTime')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>{t('explorer.type')}</TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={filters.sortBy === 'amount'}
                                                direction={filters.sortBy === 'amount' ? filters.sortOrder : 'asc'}
                                                onClick={() => handleSort('amount')}
                                            >
                                                {t('explorer.amountBTC')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={filters.sortBy === 'user'}
                                                direction={filters.sortBy === 'user' ? filters.sortOrder : 'asc'}
                                                onClick={() => handleSort('user')}
                                            >
                                                {t('explorer.user')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>{t('explorer.address')}</TableCell>
                                        <TableCell>{t('explorer.txID')}</TableCell>
                                        <TableCell>{t('explorer.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                                <CircularProgress />
                                            </TableCell>
                                        </TableRow>
                                    ) : transactions.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                                <Typography color="text.secondary">
                                                    {t('explorer.noTransactionsFound')}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        transactions.map((tx, index) => (
                                            <TableRow key={`${ tx.type }-${ index }`} hover>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2">
                                                            {formatDate(tx.time)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={tx.type}
                                                        color={tx.type === 'deposit' ? 'success' : 'warning'}
                                                        size="small"
                                                        icon={tx.type === 'deposit' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                                        {formatBTC(tx.amount)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title="View user details">
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                cursor: 'pointer',
                                                                '&:hover': { color: 'primary.main' }
                                                            }}
                                                            onClick={() => window.open(`/explorer/user/${ tx.user }`)}
                                                        >
                                                            <PersonIcon sx={{ fontSize: 16 }} />
                                                            <Typography variant="body2" color="primary">
                                                                {tx.user}
                                                            </Typography>
                                                        </Box>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        {tx.type === 'deposit' ? (
                                                            tx.senderAddresses && tx.senderAddresses.length > 0 ? (
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                    {tx.senderAddresses.map((address, index) => (
                                                                        <Typography
                                                                            key={index}
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontFamily: 'monospace',
                                                                                fontSize: '0.8rem',
                                                                                cursor: 'pointer',
                                                                                '&:hover': { textDecoration: 'underline', color: 'primary.main' }
                                                                            }}
                                                                            onClick={() => window.open(`/explorer/address/${ address }`)}
                                                                        >
                                                                            {truncateAddress(address)}
                                                                        </Typography>
                                                                    ))}
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" sx={{
                                                                    fontFamily: 'monospace', fontSize: '0.8rem',
                                                                    cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: 'primary.main' }
                                                                }}
                                                                    onClick={() => window.open(`/explorer/address/${ tx.address }`)}
                                                                >
                                                                    {truncateAddress(tx.address)}
                                                                </Typography>
                                                            )
                                                        ) : (
                                                            <Typography variant="body2" sx={{
                                                                fontFamily: 'monospace', fontSize: '0.8rem',
                                                                cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: 'primary.main' }
                                                            }}
                                                                onClick={() => window.open(`/explorer/address/${ tx.withdrawAddress }`)}
                                                            >
                                                                {truncateAddress(tx.withdrawAddress)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                        {truncateAddress(tx.txId, 16)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => window.open(`/explorer/user/${ tx.user }`)}
                                                        >
                                                            {t('explorer.viewUser')}
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="secondary"
                                                            onClick={() => window.open(`/explorer/transaction/${ tx.txId }`)}
                                                        >
                                                            {t('explorer.viewTX')}
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        /* Grid View */
                        <Box sx={{ p: 3 }}>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                    <CircularProgress />
                                </Box>
                            ) : transactions.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 8 }}>
                                    <Typography color="text.secondary">
                                        {t('explorer.noTransactionsFound')}
                                    </Typography>
                                </Box>
                            ) : (
                                <Grid container spacing={3}>
                                    {transactions.map((tx, index) => (
                                        <Grid item xs={12} sm={6} lg={4} key={`${ tx.type }-${ index }`}>
                                            <Card
                                                sx={{
                                                    height: '100%',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: '0 8px 25px rgba(255, 111, 0, 0.2)',
                                                    }
                                                }}
                                            >
                                                <CardContent>
                                                    {/* Header with Type and Date */}
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                        <Chip
                                                            label={tx.type}
                                                            color={tx.type === 'deposit' ? 'success' : 'warning'}
                                                            size="small"
                                                            icon={tx.type === 'deposit' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                                        />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatDate(tx.time)}
                                                        </Typography>
                                                    </Box>

                                                    {/* Amount */}
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="h6" sx={{
                                                            fontFamily: 'monospace',
                                                            fontWeight: 700,
                                                            color: tx.type === 'deposit' ? 'success.main' : 'warning.main'
                                                        }}>
                                                            {formatBTC(tx.amount)}
                                                        </Typography>
                                                    </Box>

                                                    {/* User */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                        <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary">{t('explorer.user')}:</Typography>
                                                        <Tooltip title="View user details">
                                                            <Typography
                                                                variant="body2"
                                                                color="primary"
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    '&:hover': { textDecoration: 'underline' }
                                                                }}
                                                                onClick={() => window.open(`/explorer/user/${ tx.user }`)}
                                                            >
                                                                {tx.user}
                                                            </Typography>
                                                        </Tooltip>
                                                    </Box>

                                                    {/* Address */}
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                                                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.2 }} />
                                                        <Typography variant="body2" color="text.secondary">{t('explorer.address')}:</Typography>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                                                            {tx.type === 'deposit' ? (
                                                                tx.senderAddresses && tx.senderAddresses.length > 0 ? (
                                                                    tx.senderAddresses.map((address, index) => (
                                                                        <Tooltip key={index} title="Copy address">
                                                                            <Box sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 0.5,
                                                                                cursor: 'pointer',
                                                                                '&:hover': { color: 'primary.main' }
                                                                            }}
                                                                                onClick={() => copyToClipboard(address)}
                                                                            >
                                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                                                    {truncateAddress(address)}
                                                                                </Typography>
                                                                                <CopyIcon sx={{ fontSize: 12 }} />
                                                                            </Box>
                                                                        </Tooltip>
                                                                    ))
                                                                ) : (
                                                                    <Tooltip title="Copy address">
                                                                        <Box sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 0.5,
                                                                            cursor: 'pointer',
                                                                            '&:hover': { color: 'primary.main' }
                                                                        }}
                                                                            onClick={() => copyToClipboard(tx.address)}
                                                                        >
                                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                                                {truncateAddress(tx.address)}
                                                                            </Typography>
                                                                            <CopyIcon sx={{ fontSize: 12 }} />
                                                                        </Box>
                                                                    </Tooltip>
                                                                )
                                                            ) : (
                                                                <Tooltip title="Copy address">
                                                                    <Box sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 0.5,
                                                                        cursor: 'pointer',
                                                                        '&:hover': { color: 'primary.main' }
                                                                    }}
                                                                        onClick={() => copyToClipboard(tx.withdrawAddress)}
                                                                    >
                                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                                            {truncateAddress(tx.withdrawAddress)}
                                                                        </Typography>
                                                                        <CopyIcon sx={{ fontSize: 12 }} />
                                                                    </Box>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    </Box>

                                                    {/* Transaction ID */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                        <WalletIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary">{t('explorer.txID')}:</Typography>
                                                        <Tooltip title="Copy transaction ID">
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                cursor: 'pointer',
                                                                '&:hover': { color: 'primary.main' }
                                                            }}
                                                                onClick={() => copyToClipboard(tx.txId)}
                                                            >
                                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                                    {truncateAddress(tx.txId, 16)}
                                                                </Typography>
                                                                <CopyIcon sx={{ fontSize: 12 }} />
                                                            </Box>
                                                        </Tooltip>
                                                    </Box>

                                                    {/* Actions */}
                                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                        <Button
                                                            fullWidth
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => window.open(`/explorer/user/${ tx.user }`)}
                                                        >
                                                            {t('explorer.viewUser')}
                                                        </Button>
                                                        <Button
                                                            fullWidth
                                                            size="small"
                                                            variant="outlined"
                                                            color="secondary"
                                                            onClick={() => window.open(`/explorer/transaction/${ tx.txId }`)}
                                                        >
                                                            {t('explorer.viewTX')}
                                                        </Button>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, newPage) => {
                                    setPage(newPage);
                                    fetchTransactionsWithTab(activeTab, filters, newPage);
                                }}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box >
    );
};

export default Explorer;
